/* eslint no-unused-vars: ["error", { "argsIgnorePattern": "^ignore" }] */
import {
  ADD_PANEL,
  ADD_WORKSPACE,
  AUTHENTICATE,
  DEAUTHENTICATE,
  DELETE_WORKSPACE,
  NOTIFY,
  SET_CHARACTERISTICS,
  SET_FILES,
  SET_LAYERS,
  SET_LOADING,
  SET_METADATA_TYPES,
  SET_MODELS,
  SET_PANEL_RAW_DATA,
  SET_PANEL_SELECTION,
  SET_REGIONS,
  SET_RUNS,
  SET_SCENARIOS,
  SET_SKIP_TOUR,
  SET_TIMESLICES,
  SET_UNITS,
  SET_USER_CONFIG,
  SET_USER_PROFILE,
  SET_WORKSPACE_PREVIEW,
  SET_WORKSPACES,
} from './mutation-types';
import {Promise} from 'es6-promise';
import {by, sortByAttrs, toMapBy} from '../utilities';
import {arraysEqual} from '../utilities/arrays';
import {flatten, get, snakeCase, omit} from 'lodash';
import {time} from '../utilities/perf';
import {getPanelComponent} from '../utilities/scenario-explorer.js';
import Map from 'collections/map';
import {cacheAction} from 'vuex-cache';
import {base64toBlob, getChartImage} from '../utilities/charts';
import {mapping} from '../components/Workspace/Panel/Timeseries/data';
import requestsFactory from './requests';
import cloneDeep from 'bootstrap-vue/esm/utils/clone-deep';

const DEFAULT_PANEL_OPTIONS = {
  name: 'New panel',
  description: '',
  size: 'full',
};

const DEFAULT_MARKDOWN = `# Text panel

You can write your text using \`markdown\`.

Follow this [link](https://guides.github.com/features/mastering-markdown) to learn more about formatting options.`;

const collectionNames = {
  metadataTypes: 'metadata categories',
  characteristics: 'variables',
};

/*
 * Wrap an action with additional logging and error handling
 */
function loadSafe(name, action, defaultValue = () => undefined) {
  return async (...args) => {
    const {commit} = args[0];
    try {
      commit(SET_LOADING, {[name]: true});
      return await action(...args);
    } catch (e) {
      commit(NOTIFY, {message: `Error loading ${collectionNames[name] || name}`, variant: 'warning', countDown: 10});
    } finally {
      commit(SET_LOADING, {[name]: false});
    }
    return defaultValue(...args);
  };
}

export function actionsBuilder(http, config) {
  const requests = requestsFactory(http, config);

  const fetchUnits = loadSafe('units', async ({commit}) => {
    const response = await http.get(`/units`);
    commit(SET_UNITS, response.data);
    return response.data;
  });

  const fetchTimeslices = loadSafe('timeslices', async ({commit}) => {
    const timeslicesEnabled = config.timeseriesPanel.tabs.some(tab => tab.key === 'timeslices');
    let timeslices = [];
    if (timeslicesEnabled) {
      const response = await http.get(`/timeslices`);
      timeslices = response.data;
    }
    commit(SET_TIMESLICES, timeslices);
    return timeslices;
  });

  const fetchRegions = loadSafe('regions', async ({commit}) => {
    const params = {hierarchy: '*'};
    const response = await http.get(`/nodes`, {params});
    commit(SET_REGIONS, response.data);
    return response.data;
  });

  const fetchModels = loadSafe('models', async ({commit}) => {
    const response = await http.get(`/models`);
    const models = response.data.sort(sortByAttrs([m => m.name.toUpperCase(), 1]));
    commit(SET_MODELS, models);
    return models;
  });

  const fetchScenarios = loadSafe('scenarios', async ({commit}) => {
    const response = await http.get(`/scenarios`);
    const scenarios = response.data.sort(sortByAttrs([s => s.name.toUpperCase(), 1]));
    commit(SET_SCENARIOS, scenarios);
    return scenarios;
  });

  const fetchRuns = loadSafe('runs', async ({commit}) => {
    const params = {getOnlyDefaultRuns: false, includeMetadata: true};
    const response = await http.get('/runs', {params});
    const runs = response.data || [];
    commit(SET_RUNS, runs);
    return runs;
  });

  const fetchRunDetails = async ({ignore}, {runId}) => {
    const response = await http.get(`/runs/${runId}`);
    return response.data;
  };

  const searchRuns = async ({ignore}, {
    defaultOnly = false,
    includeMetadata = true,
    modelId,
    scenarioId,
    perPage = 1000,
    currentPage = 1,
    sortBy = 'run_id',
    sortDesc = false,
  }) => {
    const params = {
      getOnlyDefaultRuns: defaultOnly,
      includeMetadata,
      modelId,
      scenarioId,
      perPage,
      pageNumber: currentPage - 1,
      sortBy,
      sortDesc,
    };
    const response = await http.get(`/runs/search`, {params});
    return response.data;
  };

  const fetchMetadataTypes = loadSafe('metadataTypes', async ({commit}) => {
    const response = await http.get(`/metadata/types`);
    const metadataTypes = response.data.map(mt => ({
      ...mt, defaultValue: () => undefined, isFetched: false,
    }));
    commit(SET_METADATA_TYPES, metadataTypes);
    return metadataTypes;
  }, ({state}) => state.data.metadataTypes || []);

  function getMetadataTypeDefaultValue(metadataType) {
    switch (metadataType.type) {
      case 'numeric value':
        return [metadataType.min, metadataType.max];
      case 'string value':
        return [...(metadataType.stringValues || [])];
      default:
        return [];
    }
  }

  async function fetchMetadataTypeInfo({commit, state}, metadataEntry) {
    const existingMetadataType = state.data.metadataTypes.find(mt => mt.name === metadataEntry.name);
    if (existingMetadataType && existingMetadataType.isFetched) {
      return [existingMetadataType];
    }
    const updated = [];
    try {
      const options = {params: {name: metadataEntry.name}};
      const response = await http.get(`/metadata/types`, options);
      const updatedTypesMap = new Map(response.data.map(mt => [mt.name, mt]));
      const allTypes = [...state.data.metadataTypes];
      allTypes.forEach((m, i) => {
        const metadataType = updatedTypesMap.get(m.name);
        if (metadataType) {
          const updatedMetadataType = {...m, ...metadataType, isFetched: true};
          updatedMetadataType.defaultValue = () => getMetadataTypeDefaultValue(updatedMetadataType);
          allTypes[i] = updatedMetadataType;
          updated.push(updatedMetadataType);
        }
      });
      commit(SET_METADATA_TYPES, allTypes);
    } catch (e) {
      commit(NOTIFY, {message: `Error loading metadata category details`, variant: 'warning', countDown: 10});
    }
    return updated;
  }

  async function setPanelSelection({commit, state, cache}, {workspaceId, panelId, selection}) {
    const {selection: existingSelection} = state.workspaces.find(by('id', workspaceId)).panels.find(by('id', panelId));
    commit(SET_PANEL_SELECTION, {workspaceId, panelId, selection});
    const needToUpdateMetadata = !arraysEqual(existingSelection.metadata, selection.metadata);
    if (needToUpdateMetadata) {
      const addedMetadata = selection.metadata
        .filter(metadataEntry => existingSelection.metadata.findIndex(by('name', metadataEntry.name)) < 0);
      addedMetadata.forEach(metadataEntry => cache.dispatch('fetchMetadataTypeInfo', metadataEntry));
    }
  }

  async function fetchTimeseries() {
    try {
      const response = await http.get('/ts');
      return response.data;
    } catch (error) {
      console.error('cannot fetch timeseries', error);
      return [];
    }
  }

  async function fetchSymbols({ignore}, {runs = [], sources = {}} = {}) {
    try {
      const url = `/datasets/sources`;
      const params = {runs, sources};
      const response = await http.post(url, params);
      return response.data;
    } catch (error) {
      console.error('cannot fetch symbols', error);
      return [];
    }
  }

  async function fetchSymbolsContent({ignore}, {runs = [], sources = {}} = {}) {
    try {
      const url = `/datasets/filter`;
      const params = {runs, sources};
      const response = await http.post(url, params);
      return response.data;
    } catch (error) {
      console.error('cannot fetch symbols content', error);
      return [];
    }
  }

  async function fetchLayers({state, commit, cache}) {
    try {
      if (!this.state.data.runs) {
        await cache.dispatch('fetchRuns');
      }
      const params = {
        runs: state.data.runs.map(run => run.run_id),
        sources: {
          '*': {
            'type': 'LAYER',
          },
        },
      };
      const response = await http.post('/datasets/sources', params);
      commit(SET_LAYERS, response.data);
      return response.data;
    } catch (error) {
      console.error('cannot fetch layers', error);
    }
  }

  const fetchCharacteristics = loadSafe('characteristics', async ({commit, state, cache}, {
    workspaceId: ignore1,
    panelId: ignore2,
    timeseries = false,
    parameters = false,
    variables = false,
    equations = false,
    layers = false,
  } = {}) => {
    // TODO: allow to fetch only needed
    // when adding new data types make sure to also update the cache
    // clearing in updateWorkspaceData()
    const timeseriesPromise = timeseries ? cache.dispatch('fetchTimeseries') : Promise.resolve([]);
    const layersPromise = layers ? cache.dispatch('fetchLayers') : Promise.resolve([]);
    const symbolsPromise = (parameters || variables || equations) ? cache.dispatch('fetchSymbols') : Promise.resolve([]);
    const [timeseriesData, symbolsData, layersData] = await Promise.all([timeseriesPromise, symbolsPromise, layersPromise]);
    const characteristics = {
      ...state.data.characteristics,
      timeseries: toMapBy('id', state.data.characteristics.timeseries, true)(timeseriesData),
      parameters: toMapBy('source', state.data.characteristics.parameters, true)(symbolsData.filter(p => p.type === 'PARAMETER')),
      variables: toMapBy('source', state.data.characteristics.variables, true)(symbolsData.filter(p => p.type === 'VARIABLE')),
      equations: toMapBy('source', state.data.characteristics.equations, true)(symbolsData.filter(p => p.type === 'EQUATION')),
      layers: layersData,
    };
    commit(SET_CHARACTERISTICS, characteristics);
    return characteristics;
  }, ({state}) => state.data.characteristics);

  async function fetchPanelData({state, dispatch}, {workspaceId, panelId}) {
    // TODO: consider moving panel object to payload to avoid searching by id
    const existingPanel = state.workspaces.find(by('id', workspaceId)).panels.find(by('id', panelId));
    if (existingPanel.options.showAs === 'text') return;

    switch (existingPanel.type) {
      case 'model':
        return dispatch('fetchModelPanelData', {workspaceId, panelId});
      case 'timeseries':
      default:
        return dispatch('fetchTimeseriesPanelData', {workspaceId, panelId});
    }
  }

  async function fetchModelPanelData({commit, state}, {workspaceId, panelId}) {
    const existingPanel = state.workspaces.find(by('id', workspaceId)).panels.find(by('id', panelId));
    if (existingPanel.options.showAs === 'text') return;
    const {selection} = existingPanel;
    const {runs} = selection;
    const {parameters = [], variables = [], equations = []} = selection.characteristics;

    if (runs.length === 0) {
      commit(NOTIFY, {
        message: 'Please select scenarios. Check <b>Scenario selection</b> tab...',
        variant: 'warning',
        countDown: 10,
      });
      commit(SET_PANEL_RAW_DATA, {workspaceId, panelId, raw: [], activeSelection: {}});
      return;
    }

    if (parameters.length > 0 || variables.length > 0 || equations.length > 0) {
      const sources = {};
      parameters.forEach(p => sources[p] = {type: 'PARAMETER'});
      variables.forEach(p => sources[p] = {type: 'VARIABLE'});
      equations.forEach(p => sources[p] = {type: 'EQUATION'});
      const filters = {runs, sources};
      const response = await http.post(`/datasets/filter`, filters);
      commit(SET_PANEL_RAW_DATA, {workspaceId, panelId, raw: response.data, activeSelection: selection});
    } else {
      commit(NOTIFY, {
        message: 'Please select variables. Check <b>Data Selection</b> tab...',
        variant: 'warning',
        countDown: 10,
      });
      commit(SET_PANEL_RAW_DATA, {workspaceId, panelId, raw: [], activeSelection: {}});
    }
  }

  async function fetchTimeseriesPanelData({commit, state}, {workspaceId, panelId}) {
    const existingPanel = state.workspaces.find(by('id', workspaceId)).panels.find(by('id', panelId));
    if (existingPanel.options.showAs === 'text') return;
    const {regions, units, characteristics, timeslices} = state.data;
    const selection = existingPanel.selection;
    const {runs} = selection;
    const {timeseries = []} = selection.characteristics;

    if (runs.length === 0) {
      commit(NOTIFY, {
        message: 'Please select scenarios. Check <b>Scenario selection</b> tab...',
        variant: 'warning',
        countDown: 10,
      });
      commit(SET_PANEL_RAW_DATA, {workspaceId, panelId, raw: [], activeSelection: {}});
      return;
    }

    if (timeseries.length > 0) {
      const {timeseries: tsData} = characteristics;
      const selectedTimeseries = selection.characteristics.timeseries.map(tsId => tsData[tsId]).filter(ts => !!ts);
      if (selectedTimeseries.length < timeseries.length) {
        commit(NOTIFY, {message: 'Non-existing timeseries is used', variant: 'warning', countDown: 10});
        return;
      }
      try {
        const regionsIdNameMap = new Map((regions || []).map(({id, name}) => [id, name]));
        const timesliceIdNameMap = new Map((timeslices || []).map(({id, name}) => [id, name]));
        const unitsIdNameMap = new Map((units || []).map(({id, name}) => [id, name]));
        const response = await http.post(`/runs/bulk/ts`, {
          filters: {
            runs: selection.runs,
            regions: selection.regions.map(id => regionsIdNameMap.get(id)),
            variables: selectedTimeseries.map(ts => ts.variable),
            units: selection.units.map(id => unitsIdNameMap.get(id)),
            years: [], // TODO
            timeslices: (selection.timeslices || []).map(id => timesliceIdNameMap.get(id)),
          },
        });
        const raw = response.data;
        if (raw.length === 0) {
          commit(NOTIFY, {message: 'No data retrieved', variant: 'warning', countDown: 10});
        }
        commit(SET_PANEL_RAW_DATA, {workspaceId, panelId, raw, activeSelection: cloneDeep(selection)});
      } catch (e) {
        console.error(e);
        commit(NOTIFY, {message: 'Error retrieving timeseries', variant: 'warning', countDown: 10});
      }
    } else {
      commit(NOTIFY, {
        message: 'Please select variables. Check <b>Data Selection</b> tab...',
        variant: 'warning',
        countDown: 10,
      });
    }
  }

  const fetchWorkspaces = loadSafe('workspaces', async ({commit, state}, {publishType, textFilter, flushNonSaved = false} = {}) => {
    const response = await http.get(`/workspaces`, {params: {publishType, textFilter}});
    // workaround to ignore erroneously created workspace without owner
    const workspaces = response.data.filter(w => w.updatedBy);
    if (flushNonSaved) {
      commit(SET_WORKSPACES, workspaces);
    } else {
      commit(SET_WORKSPACES, [...workspaces, ...state.workspaces.filter(w => !w.updatedBy)]);
    }
  });

  async function fetchWorkspace(ignore, {accessToken}) {
    const response = await http.get(`/workspaces/shared/${accessToken}`);
    const workspace = response.data;
    if (workspace.figures) {
      workspace.panels = workspace.figures;
      delete workspace.figures;
    }
    return workspace;
  }

  async function fetchWorkspacePreview(ignore, {workspaceId}) {
    const response = await http.get(`/workspaces/${workspaceId}/preview`, {responseType: 'blob'});
    if (response.status === 204) return null;
    return response.data;
  }

  async function downloadRunTimeseriesAsXlsx(ignore, {runId}) {
    const response = await http.get(`/runs/${runId}/ts`, {
      responseType: 'blob',
      headers: {'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}
    });
    if (response.status === 204) return null;
    return response.data;
  }

  async function updateWorkspacePreview(ignore, {workspaceId, preview}) {
    if (!preview) return;
    const idx = preview.indexOf('base64,') + 'base64,'.length;
    const content = preview.substring(idx);
    const blob = base64toBlob(content);
    await http.put(`/workspaces/${workspaceId}/preview`, blob, {headers: {'content-type': 'application/octet-stream'}});
  }

  async function createWorkspace({state, commit}, {workspace}) {
    const newWorkspace = {
      name: 'New workspace',
      description: '',
      preview: '',
      panels: [],
      publishType: 'PRIVATE',
      ...workspace,
      id: Math.max(0, ...state.workspaces.map(w => w.id)) + 1,
    };
    commit(ADD_WORKSPACE, newWorkspace);
    return newWorkspace;
  }

  async function updateWorkspaceToLatest({state, commit, dispatch}, {workspaceId}) {
    commit(SET_LOADING, {workspaces: true});
    const existingWorkspace = {...state.workspaces.find(by('id', workspaceId))};
    const runs = state.data.runs;
    const getKey = r => r.model + '|' + r.scenario;
    const defaultVersions = Object.fromEntries(runs.filter(r => r.is_default).map(r => [getKey(r), r.run_id]));
    const update = Object.fromEntries(runs.map(r => [r.run_id, defaultVersions[getKey(r)] || r.run_id]));
    const panels = existingWorkspace.panels.map(p => {
      if (getPanelComponent(p) === 'timeseries-panel') {
        const updatedRuns = p.selection.runs.map(id => update[id]);
        return {...p, selection: {...p.selection, runs: updatedRuns}};
      } else {
        return p;
      }
    });
    const updatedWorkspace = {...existingWorkspace, panels: panels};
    const workspaces = state.workspaces.map(w => w.id !== workspaceId ? w : updatedWorkspace);
    commit(SET_WORKSPACES, workspaces);
    const panelDataPromises = updatedWorkspace.panels.map(({id: panelId}) => dispatch('fetchPanelData', {workspaceId, panelId}));
    await Promise.all(panelDataPromises);
    commit(SET_LOADING, {workspaces: false});
  }

  async function saveWorkspace({state, commit, dispatch}, {workspaceId}) {
    commit(SET_LOADING, {workspaces: true});
    const workspace = state.workspaces.find(by('id', workspaceId));
    const {updatedAt, panels, hasPreview} = workspace;
    const isNew = !updatedAt;
    const url = isNew ? '/workspaces' : `/workspaces/${workspaceId}`;
    const verb = isNew ? 'post' : 'put';
    let updatedWorkspace = workspace;
    try {
      const requestOptions = {
        description: '',
        ...workspace,
        preview: undefined,
        panels: panels.map(panel => ({...omit(panel, ['activeSelection']), raw: []})),
      };

      // generate new preview if needed
      if (!hasPreview) {
        const chartPanel = p => p.options.showAs.startsWith('chart-');
        const panel = panels.find(chartPanel);
        if (panel) {
          const type = panel.options.showAs.split('-')[1];
          const {characteristics, runs} = state.data;
          const scenariosMap = Object.fromEntries(state.data.scenarios.map(({id, name}) => [id, name]));
          const modelsMap = Object.fromEntries(state.data.models.map(({id, name}) => [id, name]));
          const regionsMap = Object.fromEntries(state.data.regions.map(({id, name}) => [id, name]));
          const timeslicesMap = Object.fromEntries(state.data.timeslices.map(({id, name}) => [id, name]));
          const mappedData = mapping(panel, {characteristics, runs, scenariosMap, modelsMap, regionsMap, timeslicesMap});
          const data = {...mappedData.data};
          const title = {display: false};
          const options = {...mappedData.options, title, copyright: null};
          const preview = getChartImage({type, data, options}, 640, 480);
          commit(SET_WORKSPACE_PREVIEW, {workspaceId: workspaceId, preview});
        }
      }

      // saving workspace itself
      const response = await http[verb](url, requestOptions);
      if (response.status === 200) {
        updatedWorkspace = response.data;

        // updating preview if exists
        const preview = state.previews[workspaceId];
        if (preview) {
          await dispatch('updateWorkspacePreview', {workspaceId: updatedWorkspace.id, preview});
          if (isNew) {
            commit(SET_WORKSPACE_PREVIEW, {workspaceId});
            commit(SET_WORKSPACE_PREVIEW, {workspaceId: updatedWorkspace.id, preview});
          }
          updatedWorkspace.hasPreview = true;
        }
        // keep raw data
        const panelsRawData = Object.fromEntries(workspace.panels.map(p => [p.id, p.raw]));
        updatedWorkspace.panels.forEach(p => p.raw = panelsRawData[p.id]);
        // replace workspace
        commit(NOTIFY, {message: 'Workspace successfully saved', variant: 'success', countDown: 10});
        const workspaces = state.workspaces.map(w => w.id !== workspaceId ? w : updatedWorkspace);
        commit(SET_WORKSPACES, workspaces);
      } else {
        commit(NOTIFY, {message: 'Saving workspace failed', variant: 'danger', countDown: 10});
      }
    } catch (e) {
      console.log({e});
      commit(NOTIFY, {message: `Saving workspace failed`, variant: 'danger', countDown: 10});
    } finally {
      commit(SET_LOADING, {workspaces: false});
    }
    return updatedWorkspace;
  }

  /*
   * Evict cache and re-fetch data of a workspace and its panels
   */
  async function updateWorkspaceData({state, cache, dispatch}, workspaceId) {
    const workspace = state.workspaces.find(by('id', workspaceId));
    cache.delete('fetchModels');
    cache.delete('fetchScenarios');
    cache.delete('fetchRuns');
    // Clear the caches that are used by fetchCharacteristics
    cache.delete('fetchLayers');
    cache.delete('fetchTimeseries');
    cache.delete('fetchSymbols');

    const commonDataPromises = [
      cache.dispatch('fetchModels'),
      cache.dispatch('fetchScenarios'),
      cache.dispatch('fetchRuns'),
    ];
    workspace.panels.forEach(panel => {
      const component = getPanelComponent(panel);
      switch (component) {
        case 'text-panel':
          break;
        case 'timeseries-panel':
          const characteristics = config.timeseriesPanel.tabs.find(tab => tab.key === 'characteristics');
          const {parameters, variables, timeseries} = characteristics.types;
          const params = {parameters, variables, timeseries};
          cache.delete('fetchCharacteristics', params);
          commonDataPromises.push(cache.dispatch('fetchCharacteristics', params));
          break;
        default:
          console.error('unsupported panel type: ', component);
      }
    });
    await Promise.all(commonDataPromises);
    const panelDataPromises = workspace.panels.map(({id: panelId}) => dispatch('fetchPanelData', {
      workspaceId,
      panelId,
    }));
    return Promise.all(panelDataPromises);
  }

  async function deleteWorkspace({state, commit}, {workspaceId, auth}) {
    const workspace = state.workspaces.find(by('id', workspaceId));
    let deletedFromServer = false;
    if (workspace.updatedAt && auth.isRegistered && (auth.username === workspace.updatedBy || auth.isAdmin)) {
      try {
        const response = await http.delete(`/workspaces/${workspaceId}`);
        if (response.status === 200) {
          deletedFromServer = true;
        } else {
          commit(NOTIFY, {message: 'Deleting workspace failed', variant: 'danger', countDown: 10});
        }
      } catch (e) {
        commit(NOTIFY, {message: 'Deleting workspace failed', variant: 'danger', countDown: 10});
      }
    } else {
      commit(NOTIFY, {message: 'Insufficient permissions, deleting workspace failed', variant: 'danger', countDown: 10});
    }
    if (deletedFromServer) {
      commit(DELETE_WORKSPACE, workspace);
      commit(NOTIFY, {message: 'Workspace successfully deleted', variant: 'success', countDown: 10});
    }
  }

  async function prefetchFixedMetadata({cache}, {fixedMetadata}) {
    const fixedMetadataSet = new Set(fixedMetadata);
    const types = await cache.dispatch('fetchMetadataTypes');
    const fixedEntries = types.filter(mt => fixedMetadataSet.has(mt.name));
    const fixedTypes = await Promise.all(fixedEntries.map(metadataEntry => cache.dispatch('fetchMetadataTypeInfo', metadataEntry)));
    return flatten(fixedTypes);
  }

  async function addPanel({state, commit, cache}, {workspaceId, type = 'timeseries'}) {
    let panel;
    const getDefaultPanelOptions = workspaceId => {
      const workspace = state.workspaces.find(w => w.id === workspaceId);
      const size = workspace.panels.length > 0 ? workspace.panels[workspace.panels.length - 1].options.size : 'full';
      return {...DEFAULT_PANEL_OPTIONS, size};
    };

    async function getSelectionOptions(fixedMetadata, preselectedRegions = undefined, preselectedTimeslices = undefined) {
      const fixedTypes = await cache.dispatch('prefetchFixedMetadata', fixedMetadata);
      const metadata = fixedTypes
        .map(m => ({name: m.name, type: m.type, value: m.defaultValue()}));
      const units = [];
      const runs = [];
      const scenarios = [];
      const models = [];
      const rangesSelection = config.timeseriesPanel.tabs.find(tab => tab.key === 'ranges');
      const yearsMin = get(rangesSelection, 'years.min', 2000);
      const yearsMax = get(rangesSelection, 'years.max', 2100);
      const ranges = {'years': [yearsMin, yearsMax]};
      const selectionOptions = {metadata, models, scenarios, runs, ranges, units};
      if (preselectedRegions) {
        const preselectedRegionNames = new Set(preselectedRegions);
        const dataRegions = await cache.dispatch('fetchRegions');
        selectionOptions['regions'] = dataRegions.filter(r => preselectedRegionNames.has(r.name)).map(r => r.id);
      }
      if (preselectedTimeslices) {
        const preselectedTimeslicesNames = new Set(preselectedTimeslices);
        const dataTimeslices = await cache.dispatch('fetchTimeslices');
        selectionOptions['timeslices'] = dataTimeslices.filter(r => preselectedTimeslicesNames.has(r.name)).map(r => r.id);
      }
      return selectionOptions;
    }

    switch (type) {
      case 'timeseries': {
        const dataPanelOptions = {
          showAs: 'chart-line',
          isEditing: true,
          stackedCharts: false,
          crossPanelLinking: [],
        };
        const tabConfigs = config.timeseriesPanel.tabs.reduce((acc, tab) => ({...acc, [tab.key]: tab}), {});
        const fixedMetadata = get(tabConfigs, 'scenarioSelection.metadata.fixed', []);
        const preselectedRegions = get(tabConfigs, 'regions.preselected', []);
        const preselectedTimeslices = get(tabConfigs, 'timeslices.preselected', []);
        const selection = await getSelectionOptions(fixedMetadata, preselectedRegions, preselectedTimeslices);
        selection['characteristics'] = {parameters: [], variables: [], timeseries: []};
        const options = {...getDefaultPanelOptions(workspaceId), ...dataPanelOptions};
        const raw = [];
        panel = {selection, raw, options, type};
        break;
      }
      case 'text': {
        const options = {...getDefaultPanelOptions(workspaceId)};
        panel = {options: {...options, showAs: 'text', markdown: DEFAULT_MARKDOWN}, type};
        break;
      }
      case 'map': {
        const options = {...getDefaultPanelOptions(workspaceId), showAs: 'map', type};
        const selection = {
          'characteristics': {parameters: [], variables: [], timeseries: []},
          'runs': [],
        };
        const raw = [];
        panel = {selection, raw, options};
        break;
      }
      case 'model': {
        const options = {...getDefaultPanelOptions(workspaceId), showAs: 'table'};
        const selection = {
          'characteristics': {parameters: [], variables: [], equations: [], timeseries: []},
          'runs': [],
        };
        panel = {options, selection, type};
        break;
      }
      case 'info': {
        const options = {...getDefaultPanelOptions(workspaceId)};
        panel = {options: {...options, showAs: 'table'}, type};
        break;
      }
      default:
        console.error(`No known panel type given: ${type}`);
        return;
    }
    commit(ADD_PANEL, {workspaceId, panel});
    const updatedWorkspace = state.workspaces.find(w => w.id === workspaceId);
    return updatedWorkspace.panels[updatedWorkspace.panels.length - 1];
  }

  async function fetchUserDetails({commit}, {silent = false, token}) {
    try {
      const userProfile = await requests.fetchUserDetails({token});
      commit(SET_USER_PROFILE, userProfile);
      return userProfile;
    } catch (e) {
      if (!silent) {
        commit(NOTIFY, {message: 'Could not load user profile', variant: 'warning', countDown: 10});
      }
    }
  }

  async function saveUserDetails({commit, state}, {country, street, organisation, email, realname, token}) {
    commit(SET_USER_PROFILE, {...state.auth.profile, country, street, organisation, email, realname});
    try {
      const {message} = await requests.saveUserDetails({country, street, organisation, email, realname, token});
      commit(NOTIFY, {message, variant: 'success', countDown: 10});
    } catch (e) {
      commit(NOTIFY, {message: 'Could not update user profile', variant: 'warning', countDown: 10});
    }
  }

  async function changePassword({commit}, {password}) {
    try {
      const {message} = await requests.changePassword({password});
      commit(NOTIFY, {message, variant: 'success', countDown: 10});
    } catch (e) {
      commit(NOTIFY, {message: 'Could not update user password', variant: 'warning', countDown: 10});
    }
  }

  async function fetchUserConfig({commit}, {silent = false, token}) {
    try {
      const config = await requests.fetchUserConfig({token});
      commit(SET_USER_CONFIG, config);
      return config;
    } catch (e) {
      console.log('err');
      if (!silent) {
        commit(NOTIFY, {message: 'Could not load user profile', variant: 'warning', countDown: 10});
      }
    }
  }

  const allowedConfigOptions = new Set(['skipTour']);

  async function saveUserConfig(store, {token, config}) {
    const url = `${config.authUrl}/config/user/${config.database}`;
    const data = {
      records: Object.entries(config).filter(([key]) => allowedConfigOptions.has(key)).map(([key, value]) => {
        const path = snakeCase(key).toUpperCase();
        switch (typeof value) {
          case 'boolean':
            return {path, type: 'BOOLEAN', value};
          case 'number':
            return {path, type: 'INTEGER', value};
          default:
            return {path, type: 'STRING', value};
        }
      }),
    };
    const headers = token ? {Authorization: 'Bearer ' + token} : {};
    try {
      const response = await http.put(url, data, {headers});
      return response.data;
    } catch (e) {
      console.error(e);
    }
  }

  async function toggleTour({dispatch, commit, state}, {skip, auth}) {
    commit(SET_SKIP_TOUR, skip);
    const {config, token} = state.auth;
    if (auth.isRegistered) {
      return dispatch('saveUserConfig', {token, config});
    }
  }

  async function fetchDocs(ignore, {keys}) {
    const response = await http.post(`/docs`, {keys});
    return response.data;
  }

  async function submitFeedback(ignore, {title, body}) {
    const response = await http.post(`/feedback`, {title, body});
    if (response.status !== 201) {
      throw new Error(response.data.message || 'Unknown error while submitting user feedback');
    }
    return response.data;
  }

  const fetchFiles = loadSafe('files', async ({commit}, {includePublic = true, includePrivate = false}) => {
    const response = await http.get(`/files`, {params: {includePublic, includePrivate}});
    const files = response.data;
    commit(SET_FILES, files);
    commit(SET_LOADING, {files: false});
    return files;
  });

  const publishFile = async ({ignore}, {filename, access}) => {
    const {accessor, accessorType, accessType, operation} = access;
    const response = await http.put(`/files/${filename}/permissions`, {accessor, accessorType, accessType, operation});
    return response.data;
  };

  async function fetchFile({ignore}, {filename, redirect = false}) {
    const response = await http.get(`/files/${encodeURI(filename)}`, {params: {redirect}});
    if (response.status !== 200) {
      throw new Error('Cannot download file');
    }
    return response.data;
  }

  function fetchRefs({ignore}, {format = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'} = {}) {
    return http.get(`/runs/refs`, {responseType: 'blob', headers: {'Accept': format}}).then(response => response.data);
  }

  async function updateFileDescription({state, commit}, {filename, description}) {
    const formData = new FormData();
    formData.append('description', description);
    const {data} = await http.put(`/files/${encodeURI(filename)}`, formData, {headers: {'Content-Type': 'multipart/form-data'}});
    const {files} = state.data;
    commit(SET_FILES, files.map(f => f.filename === filename ? data : f));
    return data;
  }

  async function createFile({dispatch}, {file, description, fetchFiles = true}) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('description', description);
    const {data: fileDetails} = await http.post('/files', formData, {headers: {'Content-Type': 'multipart/form-data'}});
    if (fetchFiles) {
      await dispatch('fetchFiles', {includePublic: false, includePrivate: true});
    }
    return fileDetails;
  }

  async function deleteFile({dispatch}, {filename}) {
    await http.delete(`/files/${encodeURI(filename)}`);
    dispatch('fetchFiles', {includePublic: false, includePrivate: true});
  }

  async function requestFile({ignore}, {filename, email, embed = false}) {
    const params = {email, embed};
    const response = await http.get(`/files/${encodeURI(filename)}`, {params});
    return response.data;
  }

  async function fetchAnnotationLog({ignore}, {runId, offset = 0, limit = -1}) {
    const response = await http.get(`/runs/${runId}/annotationlog`, {params: {offset, limit}});
    return {items: response.data, total: parseInt(response.headers['x-total'], 10)};
  }

  async function fetchChangeLog({ignore}, {annotationId, offset = 0, limit = -1}) {
    const response = await http.get(`/annotations/${annotationId}/changelog`, {params: {offset, limit}});
    return {items: response.data, total: parseInt(response.headers['x-total'], 10)};
  }

  async function submitJob({ignore}, {parameters}) {
    const response = await http.post('/jobs', {parameters});
    return response.data;
  }

  async function deleteJob({ignore}, {id}) {
    const response = await http.delete(`/jobs/${id}`);
    return response.data;
  }

  async function fetchJobs({ignore}, {since, till, status} = {}) {
    const response = await http.get('/jobs', {params: {since, till, status}});
    return response.data;
  }

  async function fetchJob({ignore}, {jobId}) {
    const response = await http.get(`/jobs/${jobId}`);
    return response.data;
  }

  async function fetchJobLog({ignore}, {jobId}) {
    const response = await http.get(`/jobs/${jobId}/log`);
    return response.data;
  }

  async function logout({commit, cache}) {
    cache.clear();
    commit(DEAUTHENTICATE);
  }

  async function renewToken({state, commit}, {silent = false} = {}) {
    try {
      const {username, token} = state.auth;
      const application = config.database;
      const updatedToken = await requests.renewToken({username, token, application});
      commit(AUTHENTICATE, {...state.auth, token: updatedToken});
      return true;
    } catch (e) {
      if (!silent) {
        const text = e.response.data.errorMessage || 'Unknown error';
        const variant = 'warning';
        commit(NOTIFY, {text, variant});
      }
      return false;
    }
  }

  async function removeSolution({commit}, {runId}) {
    try {
      await requests.removeSolution({runId});
      commit(NOTIFY, {message: 'Solution successfully removed', variant: 'success', countDown: 10});
    } catch (e) {
      commit(NOTIFY, {message: 'Could not remove solution', variant: 'warning', countDown: 10});
    }
  }

  async function lockScenario({commit}, {runId}) {
    try {
      await requests.lockScenario({runId});
      commit(NOTIFY, {message: 'Scenario successfully locked', variant: 'success', countDown: 10});
    } catch (e) {
      commit(NOTIFY, {message: 'Could not lock scenario', variant: 'warning', countDown: 10});
    }
  }

  async function unlockScenario({commit}, {runId}) {
    try {
      await requests.unlockScenario({runId});
      commit(NOTIFY, {message: 'Scenario successfully unlocked', variant: 'success', countDown: 10});
    } catch (e) {
      commit(NOTIFY, {message: 'Could not unlock scenario', variant: 'warning', countDown: 10});
    }
  }

  async function removeScenario({commit, cache}, {runId}) {
    try {
      await requests.removeScenario({runId});
      cache.clear();
      commit(NOTIFY, {message: 'Scenario successfully removed', variant: 'success', countDown: 10});
    } catch (e) {
      commit(NOTIFY, {message: 'Could not remove scenario', variant: 'warning', countDown: 10});
    }
  }

  async function setScenarioAsDefault({commit}, {runId}) {
    try {
      await requests.setScenarioAsDefault({runId});
      commit(NOTIFY, {message: 'Scenario version successfully set as default', variant: 'success', countDown: 10});
    } catch (e) {
      commit(NOTIFY, {message: 'Could not set default scenario version', variant: 'warning', countDown: 10});
    }
  }

  async function unsetScenarioAsDefault({commit}, {runId}) {
    try {
      await requests.unsetScenarioAsDefault({runId});
      commit(NOTIFY, {message: 'Scenario version default flag successfully removed', variant: 'success', countDown: 10});
    } catch (e) {
      commit(NOTIFY, {message: 'Could not unset default scenario version', variant: 'warning', countDown: 10});
    }
  }

  async function checkUserAccess({ignore}, {token, permissions}) {
    try {
      return await requests.checkUserAccess({token, permissions});
    } catch (e) {
      console.error('Could not check user permissions', e);
    }
  }

  async function fetchCaches({ignore}) {
    const response = await http.get('/caches');
    return response.data;
  }

  async function cleanCache({ignore}, {cacheName}) {
    await http.delete(`/caches/${encodeURI(cacheName)}`);
  }

  const actions = {
    fetchTimeseries,
    fetchSymbols,
    fetchSymbolsContent,
    fetchLayers,
    fetchRegions,
    fetchModels,
    fetchScenarios,
    fetchRuns,
    fetchRunDetails,
    fetchMetadataTypes,
    fetchMetadataTypeInfo,
    setPanelSelection,
    fetchPanelData,
    fetchModelPanelData,
    fetchTimeseriesPanelData,
    fetchWorkspaces,
    fetchWorkspace,
    createWorkspace,
    updateWorkspaceToLatest,
    saveWorkspace,
    updateWorkspaceData,
    deleteWorkspace,
    prefetchFixedMetadata,
    addPanel,
    fetchUserDetails,
    saveUserDetails,
    changePassword,
    fetchDocs,
    submitFeedback,
    fetchUserConfig,
    saveUserConfig,
    toggleTour,
    fetchFiles,
    fetchRefs,
    publishFile,
    fetchFile,
    requestFile,
    fetchChangeLog,
    fetchAnnotationLog,
    fetchUnits,
    fetchTimeslices,
    fetchCharacteristics,
    updateFileDescription,
    createFile,
    deleteFile,
    submitJob,
    deleteJob,
    fetchJobs,
    fetchJob,
    fetchJobLog,
    logout,
    fetchWorkspacePreview,
    updateWorkspacePreview,
    renewToken,
    searchRuns,
    removeSolution,
    lockScenario,
    unlockScenario,
    removeScenario,
    setScenarioAsDefault,
    unsetScenarioAsDefault,
    checkUserAccess,
    fetchCaches,
    cleanCache,
    downloadRunTimeseriesAsXlsx,
  };

  // cachedActions receive the additional 'cache' context (to leverage caching capabilities use mapCacheActions)
  // updateWorkspaceData is present in the list as it needs to access caches to invalidate
  const cachedActions = new Set([
    'setPanelSelection', 'fetchCharacteristics', 'fetchLayers', 'logout', 'prefetchFixedMetadata', 'addPanel',
    'checkUserAccess',
    'updateWorkspaceData', 'removeScenario',
  ]);
  const wrap = (func, key) => cachedActions.has(key) ? time(cacheAction(func), key) : time(func, key);
  const wrapReducer = (acc, [key, func]) => ({...acc, [key]: wrap(func, key)});
  return Object.entries(actions).reduce(wrapReducer, {});
}
